import { FoodsSelect, HelperBox, PrimaryDialog, Typography } from "@foodpilot/foods";
import { Stack, useTheme } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { BaseStringUpload } from "../BaseStringUpload";
import { useUpsertAppParam } from "src/api/client-api/appParams/useUpsertAppParam";
import { useApplicationParameters } from "src/api/client-api/appParams";
import { ACTION_PLAN_MODES, appParamCacheKey } from "src/api/client-api/appParams/useApplicationParameters";
import { useQueryClient } from "react-query";
import { BaseSelectUpload } from "../BaseSelectUpload";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useGetAllActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetAllActionPlan";

type ActionPlanModalProps = {
    isOpen: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
};
export const ActionPlanModal = (props: ActionPlanModalProps) => {
    const { isOpen, setOpen } = props;
    const { t } = useTranslation();

    return (
        <PrimaryDialog
            open={isOpen}
            setOpen={setOpen}
            onValidate={() => {
                // Makes sure the app param are shown immediately
                location.reload();
            }}
            onCancel={() => {}}
            options={{
                title: <Typography variant="h4">{t("actionPlan.trajectory.form.title")}</Typography>,
                content: <ActionPlanConfiguration />,
            }}
            cancelText={t("primaryDialog.cancel")}
            validateText={t("primaryDialog.validate")}
        />
    );
};

const ActionPlanConfiguration = () => {
    const { isActionPlanSimplified } = useApplicationParameters();
    const isSimplified = isActionPlanSimplified();
    const theme = useTheme();

    return (
        <Stack padding={theme.spacing(2, 4, 4, 4)} gap={theme.spacing(3)}>
            <SelectField />
            {isSimplified === false ?
                <SimplifiedSettings />
            :   <DetailedSettings />}
            <BaseStringUpload
                appParamKey="actionPlan-scenario-with"
                label="Name of your plan after taking actions"
                placeholder="Name by default"
                description="Max 32 characters"
                forceReload={false}
            />
        </Stack>
    );
};

const SimplifiedSettings = () => {
    return (
        <BaseStringUpload
            appParamKey="actionPlan-scenario-without"
            label="Name of your plan without taking actions"
            placeholder="Name by default"
            description="Max 32 characters"
            forceReload={false}
        />
    );
};

const DetailedSettings = () => {
    const { campaigns } = useFoodpilotContext();
    const { data: actionPlans, isLoading } = useGetAllActionPlan();
    const theme = useTheme();

    if (isLoading) return;
    if (actionPlans === undefined) return;

    const cannotAlter = actionPlans.length > 0;
    const allCampaigns = campaigns.map((c) => {
        return {
            id: c["@id"] ?? "shouldnthappen",
            label: c.label,
            valueToStore: c["@id"] ?? "shouldnthappen",
        };
    });

    return (
        <Stack>
            <BaseSelectUpload
                title={"Target campaign"}
                appParamKey="actionPlan-targetCampaign"
                allChoices={allCampaigns}
                disabled={cannotAlter}
            />
            {cannotAlter && (
                <HelperBox>
                    <Stack gap={theme.spacing(1)}>
                        <Typography variant="body-bold">
                            {"You already started an Action Plan, you cannot change this parameter anymore"}
                        </Typography>
                        <Typography variant="small-bold">{"Contact your administrator."}</Typography>
                    </Stack>
                </HelperBox>
            )}
        </Stack>
    );
};

const SelectField = () => {
    const { t } = useTranslation();
    const { data: actionPlans, isLoading } = useGetAllActionPlan();

    const queryClient = useQueryClient();
    const upsertAppParam = useUpsertAppParam({ forceReload: false });
    const { applicationParameters } = useApplicationParameters();
    const originalValue = applicationParameters?.["actionPlan-mode"];

    const allModes = ACTION_PLAN_MODES;
    const foundMode = allModes.find((v) => v === originalValue) ?? null;
    const [selectedValue, setSelectedValue] = useState(foundMode);

    if (isLoading) return;
    if (actionPlans === undefined) return;

    const cannotAlter = actionPlans.length > 0;

    return (
        <Stack>
            <Typography variant="big-bold">{`Action Plan mode`}</Typography>
            <FoodsSelect
                selectedOption={selectedValue}
                options={[...allModes]}
                disabled={cannotAlter}
                onChange={(selectedItem) => {
                    if (selectedItem === null) return;

                    upsertAppParam.mutate(
                        {
                            isNew: foundMode === null ? true : false,
                            key: "actionPlan-mode",
                            value: selectedItem,
                        },
                        {
                            onSuccess: () => {
                                queryClient.refetchQueries(appParamCacheKey);
                                setSelectedValue(selectedItem);
                            },
                        },
                    );
                }}
                getId={(item) => item}
                getName={(item) => item}
                textOptions={{
                    placeholder: t("component.select.placeholder"),
                    searchPlaceholder: t("component.select.searchPlaceholder"),
                    emptyOptionsText: t("component.select.noOptions"),
                    defaultValueText: t("component.select.defaultValue"),
                    noResult: t("component.select.noResultFound"),
                }}
            />
        </Stack>
    );
};
